import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Sparkles, Loader2, Languages, ChevronDown } from "lucide-react";

// Little inline "AI assist" widget that sits above the message textarea
// inside ContribPanel. Users pick a language + tone, add optional hints,
// hit "Draft in {language}", and the resulting paragraph is written into
// the textarea (via onDraft) so they can edit before submitting.
//
// Props:
//   memorialKey — slug or id, used for the API call
//   kind        — "guestbook" | "memory" | "eulogy" (default: "memory")
//   onDraft(text, language)  — called when a draft comes back
//   defaultRelationship — optional pre-fill for the relationship input
export default function AiMessageAssist({ memorialKey, kind = "memory", onDraft, defaultRelationship = "" }) {
  const [open, setOpen] = useState(false);
  const [languages, setLanguages] = useState([{ code: "en", name: "English" }]);
  const [language, setLanguage] = useState("en");
  const [tone, setTone] = useState("warm");
  const [relationship, setRelationship] = useState(defaultRelationship);
  const [hints, setHints] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/memorial-messages/languages")
      .then((r) => setLanguages(r.data?.languages || languages))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const langName = (languages.find((l) => l.code === language) || {}).name || "English";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/memorials/${memorialKey}/message/ai-draft`, {
        kind, language, tone,
        relationship: relationship || null,
        hints: hints || null,
      });
      if (data?.draft) {
        onDraft?.(data.draft, language);
        toast.success(`Draft ready in ${langName} — edit before posting.`);
      } else {
        toast.error("No draft was returned — please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't draft a message. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-2xl border overflow-hidden mb-3"
      data-testid="ai-assist-panel"
      style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #FEF3C7 0%, #FFFFFF 100%)" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-assist-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/50"
      >
        <span className="inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: "#B45309" }} />
          <span className="font-display text-sm">Not sure what to write? Let AI help — in any language.</span>
        </span>
        <ChevronDown className={`w-4 h-4 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t bg-white/40" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="text-xs font-mono uppercase tracking-widest opacity-70">
              <div className="mb-1 flex items-center gap-1">
                <Languages className="w-3 h-3" /> Language
              </div>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="ai-assist-language"
                className="w-full rounded-lg border px-2.5 py-1.5 text-sm bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-mono uppercase tracking-widest opacity-70">
              <div className="mb-1">Tone</div>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                data-testid="ai-assist-tone"
                className="w-full rounded-lg border px-2.5 py-1.5 text-sm bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                <option value="warm">Warm</option>
                <option value="brief">Brief</option>
                <option value="poetic">Poetic</option>
                <option value="spiritual">Spiritual</option>
              </select>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="text-xs font-mono uppercase tracking-widest opacity-70">
              <div className="mb-1">Your relationship (optional)</div>
              <input
                type="text"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                placeholder="friend, cousin, neighbour…"
                data-testid="ai-assist-relationship"
                maxLength={80}
                className="w-full rounded-lg border px-2.5 py-1.5 text-sm bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
              />
            </label>
            <label className="text-xs font-mono uppercase tracking-widest opacity-70">
              <div className="mb-1">A detail to weave in (optional)</div>
              <input
                type="text"
                value={hints}
                onChange={(e) => setHints(e.target.value)}
                placeholder="loved kites; grew mangoes; sang at church"
                data-testid="ai-assist-hints"
                maxLength={400}
                className="w-full rounded-lg border px-2.5 py-1.5 text-sm bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
              />
            </label>
          </div>
          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="text-[11px] leading-snug opacity-70 max-w-md">
              AI never invents facts — it only uses what's on this memorial + hints you add.
              You can edit the draft before it's posted.
            </p>
            <button
              type="button"
              onClick={generate}
              disabled={busy}
              data-testid="ai-assist-generate"
              className="wofy-btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Draft in {langName}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
